import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { getJson } from "@/lib/api"
import { captureEvent } from "@/lib/analytics"
import type {
  FieldTestAssignmentResponse,
  ReportsSignupWallProps,
} from "@/types/reportsSignupWall"
import {
  REPORTS_SIGNUP_WALL_EXPERIMENT,
  REPORTS_SIGNUP_WALL_REVEAL_DELAY_MS,
  REPORTS_SIGNUP_WALL_VARIANT,
} from "@/types/reportsSignupWall"

const GATED_CLASSES = [
  "relative",
  "max-h-96",
  "overflow-hidden",
  "blur-lg",
  "select-none",
  "pointer-events-none",
] as const

function assignmentUrl(experimentUrl: string): string {
  const url = new URL(experimentUrl, window.location.origin)
  const pageParams = new URLSearchParams(window.location.search)
  pageParams.forEach((value, key) => {
    if (key.startsWith("field_test")) {
      url.searchParams.set(key, value)
    }
  })
  return url.toString()
}

function captureWallCta(cta: "sign_up" | "sign_in", experimentName: string, variant: string): void {
  captureEvent("reports_signup_wall_cta_clicked", {
    page_url: window.location.href,
    experiment: experimentName,
    variant,
    cta,
  })
}

function captureWallShown(experimentName: string, variant: string): void {
  captureEvent("reports_signup_wall_shown", {
    page_url: window.location.href,
    experiment: experimentName,
    variant,
    conversion_goal: "sign_in_or_sign_up",
  })
}

export function ReportsSignupWall({
  experimentUrl,
  contentSelector,
  signUpUrl,
  signInUrl,
  heading,
  description,
  benefits,
  experimentName = REPORTS_SIGNUP_WALL_EXPERIMENT,
  signupWallVariant = REPORTS_SIGNUP_WALL_VARIANT,
  signedOut = true,
  revealDelayMs = REPORTS_SIGNUP_WALL_REVEAL_DELAY_MS,
  shipped = false,
}: ReportsSignupWallProps): JSX.Element | null {
  const [showWall, setShowWall] = useState(false)

  useEffect(() => {
    if (!signedOut) return undefined

    let cancelled = false
    let revealTimer: number | undefined

    const reveal = (variant: string) => {
      revealTimer = window.setTimeout(() => {
        if (cancelled) return
        setShowWall(true)
        captureWallShown(experimentName, variant)
      }, Math.max(0, revealDelayMs))
    }

    async function loadVariant(): Promise<void> {
      if (shipped) {
        reveal(signupWallVariant)
        return
      }

      try {
        const data = await getJson<FieldTestAssignmentResponse>(assignmentUrl(experimentUrl))
        if (cancelled) return
        if (!data.enrolled || data.variant !== signupWallVariant) return

        reveal(signupWallVariant)
      } catch {
        // Keep report content available if assignment fails while the test is live.
      }
    }

    void loadVariant()
    return () => {
      cancelled = true
      if (revealTimer !== undefined) window.clearTimeout(revealTimer)
    }
  }, [experimentUrl, experimentName, signupWallVariant, signedOut, revealDelayMs, shipped])

  useEffect(() => {
    if (!showWall || !contentSelector) return undefined

    const content = document.querySelector<HTMLElement>(contentSelector)
    if (!content) return undefined

    // Blur only the report body — never the modal (portaled to document.body).
    content.classList.add(...GATED_CLASSES)

    const previousOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"

    return () => {
      content.classList.remove(...GATED_CLASSES)
      document.documentElement.style.overflow = previousOverflow
    }
  }, [showWall, contentSelector])

  if (!showWall) return null

  return createPortal(
    <div data-testid="reports-signup-wall">
      <div className="fixed inset-0 z-40 bg-slate-950/50" aria-hidden="true" />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reports-signup-wall-title"
      >
        <div
          className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-gray-200 sm:p-8"
          onClick={(event) => event.stopPropagation()}
        >
          <h2 id="reports-signup-wall-title" className="text-xl font-semibold text-gray-900">
            {heading}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{description}</p>
          <div className="mt-3">
            <p className="mb-2 text-xs font-medium text-gray-700">Free account includes:</p>
            <ul className="space-y-1.5">
              {benefits.map((benefit) => (
                <li key={benefit} className="flex gap-2 text-xs text-gray-600">
                  <svg
                    className="h-4 w-4 shrink-0 text-indigo-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-6 flex flex-col gap-3">
            <a
              href={signUpUrl}
              className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={() => captureWallCta("sign_up", experimentName, signupWallVariant)}
            >
              Sign up free
            </a>
            <a
              href={signInUrl}
              className="inline-flex w-full items-center justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={() => captureWallCta("sign_in", experimentName, signupWallVariant)}
            >
              Log in
            </a>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
